<script setup lang="ts">
/**
 * The signed-in shell: a familiar social-network layout.
 *
 *   ┌── top bar: brand · search · messages · notifications · account ──────┐
 *   ├── nav row: labelled tabs, underlined on the current one ─────────────┤
 *   │ left rail (xl+) │       centre column        │ contacts rail (lg+)   │
 *   └───────────────────────────────────────────────────────────────────────┘
 *
 * The rails belong to the **feed and nothing else**. Everywhere else the
 * navigation in the bar above is the same navigation the left rail repeats,
 * and a column of it beside a page you came to read is a second copy of the
 * menu you just used.
 *
 * The nav row is a second bar rather than icons squeezed into the first,
 * which is what the design asked for and what makes the tabs readable in
 * Thai — "โปรไฟล์ของฉัน" does not fit under a 20px icon.
 *
 * **Nothing appears in both bars.** Each destination says where it belongs and
 * the three lists are filtered from that one array, so a tab cannot drift into
 * two of them or out of all of them.
 *
 *   - Messages and Notifications are in the **top bar**: they carry a live
 *     count, which is what makes them worth a permanent icon, and they were
 *     repeated in the nav row directly beneath — the same destination on
 *     screen twice, one row apart.
 *   - Discover is in neither bar. The search field in the top bar submits to
 *     `/discover`, so the tab underneath it was a second button for the box
 *     immediately above it.
 *   - The **mobile tab bar** keeps all four, because it replaces the nav row
 *     entirely below `md` and a thumb reaches the bottom of a phone, not the
 *     top.
 *
 * Every tab goes somewhere that exists. The design also had a Marketplace
 * tab and a Pages tab; there is no marketplace, and pages have no index
 * route to open — only the ones you own, which the left rail lists.
 *
 * The rails belong to the layout, not to each view, so they persist across
 * navigation and keep their scroll position. A route opts out with
 * `meta.shell`:
 *   'feed' — both rails (home)
 *   'wide' — left rail only, roomier centre (default)
 *   'full' — no rails, no chrome (chat owns the viewport)
 *
 * Below `lg` the rails disappear and the bottom tab bar takes over, because
 * thumbs reach the bottom of a phone and not the top.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';

import Avatar from '../components/Avatar.vue';
import ContactsRail from '../components/ContactsRail.vue';
import NavIcon, { type IconName } from '../components/NavIcon.vue';
import NotificationBadge from '../components/NotificationBadge.vue';
import SideRail from '../components/SideRail.vue';
import SoonBadge from '../components/SoonBadge.vue';
import { isDesktopShell } from '../utils/desktop-shell.ts';
import { useAuthStore } from '../stores/auth.ts';
import { useChatStore } from '../stores/chat.ts';
import { useNotificationStore } from '../stores/notification.ts';
import { usePresenceStore } from '../stores/presence.ts';
import { useRealtimeStore } from '../stores/realtime.ts';
import { handle } from '../utils/format.ts';
import { useI18n } from '../i18n/index.ts';

const auth = useAuthStore();
const { t } = useI18n();
const chat = useChatStore();
const notifications = useNotificationStore();
const realtime = useRealtimeStore();
const presence = usePresenceStore();
const route = useRoute();
const router = useRouter();

const menuOpen = ref(false);
const verifyBannerDismissed = ref(false);
const search = ref('');

/**
 * Which bars a destination appears in.
 *
 *   'top'    — the icon group in the top bar. Worth it only with a live count.
 *   'row'    — the labelled nav row beneath it.
 *   'mobile' — the bottom tab bar, which replaces the nav row below `md`.
 *
 * `top` and `row` together would be the same link twice, one row apart; a test
 * fails if any destination claims both.
 */
type NavPlace = 'top' | 'row' | 'mobile';

interface NavItem {
  name: string;
  to: string;
  label: string;
  icon: IconName;
  badge?: () => number;
  places: NavPlace[];
}

const NAV = computed<NavItem[]>(() => [
  { name: 'home', to: '/', label: t('nav.home'), icon: 'home', places: ['row', 'mobile'] },
  {
    // Not in the nav row: the search field in the top bar submits here, so a
    // tab underneath it was a second button for the box directly above.
    name: 'discover',
    to: '/discover',
    label: t('nav.discover'),
    icon: 'discover',
    places: ['mobile'],
  },
  {
    name: 'messages',
    to: '/messages',
    label: t('nav.messages'),
    icon: 'messages',
    places: ['top', 'mobile'],
    badge: () => chat.totalUnread,
  },
  {
    name: 'marketplace',
    to: '/marketplace',
    label: t('nav.marketplace'),
    icon: 'store',
    places: ['row'],
  },
  { name: 'friends', to: '/friends', label: t('nav.friends'), icon: 'friends', places: ['row'] },
  {
    // Took the slot the disabled "Pages" tab held. Pages has an API and still
    // no index route, so it went back to being nothing rather than staying a
    // button that goes nowhere; games have both.
    name: 'games',
    to: '/games',
    label: t('nav.games'),
    icon: 'game',
    places: ['row'],
  },
  {
    // The hub, not a group. `/groups` answered 404 until it existed, from a tab the
    // navigation itself drew.
    name: 'groups',
    to: '/groups',
    label: t('nav.groups'),
    icon: 'friends',
    places: ['row'],
  },
  {
    name: 'notifications',
    to: '/notifications',
    label: t('nav.notifications'),
    icon: 'notifications',
    places: ['top', 'mobile'],
    badge: () => notifications.unreadCount,
  },
]);

/**
 * Tabs from the design whose systems are not built yet.
 *
 * Present so the shell matches the design, `disabled` so nobody is sent
 * anywhere that does not exist. Each becomes an ordinary `NAV` entry the day it
 * has a route — as Marketplace did, and as Games has now.
 *
 * **Empty, and that is the point.** Games took the slot the disabled Pages tab
 * held. Pages has an API and no index route to open, so rather than leave a
 * button that goes nowhere sitting next to one that works, it is simply not
 * drawn: a nav row where one of five tabs is decorative teaches people not to
 * trust the other four. It comes back as an ordinary entry the day it has a
 * page, which is one line here.
 */
const SOON_TABS = computed<{ name: string; label: string; icon: IconName }[]>(() => []);

/** The account's own profile, which is a tab in the design and a route here. */
const profilePath = computed(() => (auth.user ? `/@${auth.user.username}` : '/settings'));

/**
 * Only *your* profile lights the tab.
 *
 * It keyed on the route name, and `/@:username` is one route for everybody —
 * so reading a stranger's page marked "My profile" as where you were.
 */
const onOwnProfile = computed(
  () => route.name === 'profile' && route.params['username'] === auth.user?.username,
);

/**
 * The two icons in the top bar, which the design puts there as well as in the
 * nav row below. Both carry a live count, which is the reason they are worth
 * repeating: they are the two things that change while you are reading.
 */
/**
 * The three bars, filtered from the one array above.
 *
 * Moving a destination between them is one edit to its `places`, so the lists
 * cannot disagree about where something lives.
 */
const inPlace = (place: NavPlace) => NAV.value.filter((item) => item.places.includes(place));

/** The top bar's icon group: the two things that change while you are reading. */
const quickActions = computed(() => inPlace('top'));

/** The labelled nav row, on `md` and up. */
/** The labelled nav row, on `md` and up. */
const navTabs = computed(() => inPlace('row'));

/**
 * The bottom tab bar, which replaces the nav row below `md` — so it still
 * carries Discover, Messages and Notifications. A thumb reaches the bottom of
 * a phone, not the top.
 */
const mobileNav = computed(() => inPlace('mobile'));

const shell = computed(() => route.meta.shell ?? 'wide');
const isFullBleed = computed(() => shell.value === 'full');
/** `page` lets the view lay out its own columns across the whole container. */

/**
 * Rails on the feed, and only the feed.
 *
 * They used to appear on every `wide` route too, which put your own
 * navigation beside every page you opened — a second copy of the menu in the
 * bar you had just used to get there. The feed is the one page where the
 * rails carry something the bar does not: who you are, and who you can talk
 * to right now.
 */
const showRails = computed(() => auth.isAuthenticated && shell.value === 'feed');
const showContacts = showRails;

/**
 * How wide the shell is.
 *
 * The feed is the only page with three columns — rail, feed, contacts — and at
 * 6xl the middle one was squeezed to about 500px to make room for the two
 * beside it. It gets the wider container back; every other page keeps 6xl,
 * which is as much as a single column of anything wants.
 *
 * Applied to all three containers together: the top bar, the nav row and the
 * content centre inside their own, and they only line up while the three agree.
 */
/**
 * One container width for every page: `max-w-7xl`.
 *
 * It was 7xl on the feed and 6xl everywhere else, on the reasoning that only the feed
 * has three columns to fit. In practice the narrower pages are not narrower *columns*
 * — the content inside them is centred by its own view — so 6xl only moved the top bar
 * and the nav row inwards relative to the feed, and switching between them nudged the
 * whole chrome sideways.
 */
const shellWidth = 'max-w-7xl';

/**
 * The desktop chat window shows the chat and nothing else.
 *
 * Every tab, the search box and the phone's tab bar lead into the rest of the
 * product, and each one of them in a chat window is somebody losing the thing
 * they opened. They are not drawn there — one flag, read in the few places that
 * draw a way out, rather than filtered out of the lists as well: two guards for
 * one fact is one guard nothing can prove.
 *
 * Read once: the user agent does not change while the page is open.
 */
const chatOnly = isDesktopShell();

/**
 * Where a rail sticks, and how tall it may be before it scrolls itself.
 *
 * Both come from `--chrome-height` (main.css), which is the top bar plus the
 * nav row. A rail that stuck at an offset smaller than that slid *under* the
 * header; one with no height limit could only be read by scrolling the feed to
 * its end, which on a laptop meant the contacts rail was never reached at all.
 */
const RAIL_STYLE = {
  top: 'var(--chrome-height)',
  maxHeight: 'calc(100dvh - var(--chrome-height))',
} as const;

const columnWidth = computed(() => {
  if (!auth.isAuthenticated || isFullBleed.value) return '';
  // The feed sits between two rails and must not outgrow them; a conversation
  // list reads badly at full width, so it keeps the same measure without them.
  if (shell.value === 'feed' || shell.value === 'narrow') return 'mx-auto max-w-[42rem]';
  // Everything else takes the container. `wide` used to clamp at `max-w-4xl`,
  // which made Settings and Notifications 896px inside a 1152px shell — a page
  // with a side column squeezed into the width of a page without one. The
  // container is the one place the app's width is decided now.
  return '';
});

const showVerifyBanner = computed(
  () =>
    auth.isAuthenticated &&
    auth.user !== null &&
    auth.user.emailVerifiedAt === null &&
    !verifyBannerDismissed.value &&
    route.name !== 'verify-email',
);

function dismissVerifyBanner(): void {
  verifyBannerDismissed.value = true;
}

function isActive(item: NavItem): boolean {
  return route.path === item.to || (item.to !== '/' && route.path.startsWith(item.to));
}

function submitSearch(): void {
  const term = search.value.trim();
  if (term.length === 0) return;
  // The server matches names as well as handles, so the term goes through
  // as typed - stripping the @ is the server's job, not the search box's.
  void router.push({ name: 'discover', query: { username: term } });
}

async function signOut(): Promise<void> {
  menuOpen.value = false;
  // Close the socket before the session goes: it is the thing that reports
  // this person as online, and leaving it open would keep them there.
  realtime.stop();
  presence.reset();
  await auth.logout();
  notifications.reset();
  await router.push({ name: 'login' });
}

function closeMenu(event: MouseEvent): void {
  const target = event.target as HTMLElement | null;
  if (!target?.closest('[data-account-menu]')) menuOpen.value = false;
}

onMounted(() => {
  document.addEventListener('click', closeMenu);
  if (auth.isAuthenticated) connect();
});

onUnmounted(() => {
  document.removeEventListener('click', closeMenu);
  notifications.stopPolling();
  // The shell unmounts when the user signs out or lands on a guest page; the
  // session socket has no reason to outlive it.
  realtime.stop();
});

/** Everything that should be running while somebody is signed in. */
function connect(): void {
  notifications.startPolling();
  realtime.start();
}

watch(
  () => auth.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      connect();
      return;
    }
    realtime.stop();
    presence.reset();
    notifications.reset();
  },
);
</script>

<template>
  <div class="flex min-h-full flex-col bg-surface">
    <a
      href="#main"
      class="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-xl focus:bg-primary focus:px-4 focus:py-2 focus:text-on-primary"
    >
      {{ t('nav.skipToContent') }}
    </a>

    <!-- ── Top bar ─────────────────────────────────────────────────────── -->
    <header class="sticky top-0 z-30 bg-surface-container-lowest/90 shadow-sm backdrop-blur-md">
      <div :class="['mx-auto flex h-16 items-center gap-space-sm px-4 sm:gap-space-md', shellWidth]">
        <RouterLink
          :to="chatOnly ? '/messages' : '/'"
          class="flex shrink-0 items-center gap-2"
          aria-label="TalkVibe"
        >
          <img src="/logo.png" alt="" class="h-8 w-8" />
          <span class="hidden text-headline-md tracking-tight text-primary sm:inline">TalkVibe</span>
        </RouterLink>

        <form
          v-if="auth.isAuthenticated && !chatOnly"
          class="mx-1 min-w-0 flex-1 sm:mx-space-md"
          role="search"
          @submit.prevent="submitSearch"
        >
          <label for="global-search" class="sr-only">{{ t('common.search') }}</label>
          <div class="relative flex items-center">
            <NavIcon
              name="search"
              class="pointer-events-none absolute left-3.5 h-5 w-5 text-outline"
            />
            <input
              id="global-search"
              v-model="search"
              type="search"
              :placeholder="t('nav.search')"
              class="w-full rounded-full bg-surface-container-low py-2 pl-10 pr-4 text-body-sm text-on-surface transition-colors placeholder:text-outline focus:bg-surface-container-lowest focus:outline-none focus:ring-2 focus:ring-primary-container"
            />
          </div>
        </form>

        <!-- Named so a test can ask what this bar offers without counting
             divs, and so the brand mark beside it is not mistaken for a tab. -->
        <div data-quick-actions class="flex shrink-0 items-center gap-1 sm:gap-1.5">
          <template v-if="auth.isAuthenticated && auth.user">
            <RouterLink
              v-for="item in quickActions"
              :key="item.name"
              :to="item.to"
              class="relative rounded-xl p-2 text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
              :aria-label="item.label"
            >
              <NavIcon :name="item.icon" class="h-6 w-6" />
              <span class="absolute right-1 top-1">
                <NotificationBadge v-if="item.badge" :count="item.badge()" />
              </span>
            </RouterLink>

            <span class="mx-1 hidden h-6 w-px bg-outline-variant/40 sm:block" aria-hidden="true" />

            <div class="relative" data-account-menu>
              <button
                type="button"
                class="flex items-center gap-1 rounded-full p-1 transition-colors hover:bg-surface-container-low"
                :aria-expanded="menuOpen"
                :aria-label="t('nav.accountMenu')"
                aria-haspopup="menu"
                @click="menuOpen = !menuOpen"
              >
                <span class="relative">
                  <Avatar
                    :name="auth.user.displayName"
                    :src="auth.user.avatarUrl"
                    :asset="auth.user.avatarAsset"
                    :user-id="auth.user.id"
                    size="sm"
                  />
                  <span
                    class="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-tertiary-container ring-2 ring-surface-container-lowest"
                    aria-hidden="true"
                  />
                </span>
                <NavIcon name="chevron" class="hidden h-4 w-4 text-on-surface-variant sm:block" />
              </button>

              <div
                v-if="menuOpen"
                class="absolute right-0 z-40 mt-2 w-60 overflow-hidden rounded-xl bg-surface-container-lowest p-1 shadow-xl ring-1 ring-outline-variant/40"
                role="menu"
              >
                <RouterLink
                  :to="profilePath"
                  class="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-surface-container-low"
                  role="menuitem"
                  @click="menuOpen = false"
                >
                  <Avatar
                    :name="auth.user.displayName"
                    :src="auth.user.avatarUrl"
                    :asset="auth.user.avatarAsset"
                    :user-id="auth.user.id"
                    size="sm"
                  />
                  <span class="min-w-0">
                    <span class="block truncate text-label-md text-on-surface">
                      {{ auth.user.displayName }}
                    </span>
                    <span class="block truncate text-body-sm text-on-surface-variant">
                      {{ handle(auth.user.username) }}
                    </span>
                  </span>
                </RouterLink>

                <hr class="my-1 border-outline-variant/40" />

                <RouterLink
                  to="/friends"
                  class="block rounded-lg px-3 py-2 text-body-md hover:bg-surface-container-low xl:hidden"
                  role="menuitem"
                  @click="menuOpen = false"
                >
                  {{ t('nav.friends') }}
                </RouterLink>

                <!--
                  Starting a page or a group, below `xl` only.
                  The left rail carries both and the rail is `hidden xl:block`,
                  so for a while there was no way at all to reach either form on
                  a phone: they had been on the feed composer and were taken off
                  it. These are that way back, and they hide at `xl` rather than
                  sitting in two places at once.
                -->
                <RouterLink
                  to="/pages/new"
                  class="block rounded-lg px-3 py-2 text-body-md hover:bg-surface-container-low xl:hidden"
                  role="menuitem"
                  @click="menuOpen = false"
                >
                  {{ t('pages.create') }}
                </RouterLink>
                <RouterLink
                  to="/groups/new"
                  class="block rounded-lg px-3 py-2 text-body-md hover:bg-surface-container-low xl:hidden"
                  role="menuitem"
                  @click="menuOpen = false"
                >
                  {{ t('group.create') }}
                </RouterLink>
                <RouterLink
                  to="/settings"
                  class="block rounded-lg px-3 py-2 text-body-md hover:bg-surface-container-low"
                  role="menuitem"
                  @click="menuOpen = false"
                >
                  {{ t('nav.settingsPrivacy') }}
                </RouterLink>
                <RouterLink
                  v-if="auth.user.role === 'moderator' || auth.user.role === 'admin'"
                  to="/moderation"
                  class="block rounded-lg px-3 py-2 text-body-md hover:bg-surface-container-low"
                  role="menuitem"
                  @click="menuOpen = false"
                >
                  {{ t('nav.moderation') }}
                </RouterLink>
                <button
                  type="button"
                  class="block w-full rounded-xl px-3 py-2 text-left text-body-md text-error hover:bg-error-container/40"
                  role="menuitem"
                  @click="signOut"
                >
                  {{ t('nav.signOut') }}
                </button>
              </div>
            </div>
          </template>

          <template v-else-if="auth.ready">
            <RouterLink
              to="/login"
              class="rounded-full px-space-md py-2 text-label-md text-on-surface-variant transition-colors hover:bg-surface-container-low"
            >
              {{ t('auth.signIn') }}
            </RouterLink>
            <RouterLink
              to="/register"
              class="rounded-full bg-primary-container px-space-md py-2 text-label-md text-on-primary transition-colors hover:bg-primary"
            >
              {{ t('auth.signUp') }}
            </RouterLink>
          </template>
        </div>
      </div>

      <!-- Nav row: a second bar so the labels fit, in Thai as well as English. -->
      <div
        v-if="auth.isAuthenticated && !chatOnly"
        class="hidden border-t border-outline-variant/30 bg-surface-container-lowest md:block"
      >
        <nav
          :class="['mx-auto flex items-center justify-center gap-1 px-4', shellWidth]"
          :aria-label="t('nav.quickNav')"
        >
          <RouterLink
            v-for="item in navTabs"
            :key="item.name"
            :to="item.to"
            class="relative flex items-center gap-2 px-4 py-3 text-label-lg transition-colors lg:px-6"
            :class="
              isActive(item)
                ? 'text-primary'
                : 'rounded-lg text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
            "
            :aria-current="isActive(item) ? 'page' : undefined"
          >
            <NavIcon :name="item.icon" :filled="isActive(item)" class="h-6 w-6" />
            <span class="hidden lg:inline">{{ item.label }}</span>
            <span
              v-if="isActive(item)"
              class="absolute inset-x-0 bottom-0 h-0.5 bg-primary"
              aria-hidden="true"
            />
          </RouterLink>

          <button
            v-for="tab in SOON_TABS"
            :key="tab.name"
            type="button"
            disabled
            class="flex cursor-not-allowed items-center gap-2 rounded-xl px-4 py-3 text-label-lg text-on-surface-variant opacity-60 lg:px-6"
            :title="t('common.notReadyYet')"
          >
            <NavIcon :name="tab.icon" class="h-6 w-6" />
            <span class="hidden lg:inline">{{ tab.label }}</span>
            <SoonBadge />
          </button>

          <RouterLink
            :to="profilePath"
            class="relative flex items-center gap-2 px-4 py-3 text-label-lg transition-colors lg:px-6"
            :class="
              onOwnProfile
                ? 'text-primary'
                : 'rounded-lg text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
            "
          >
            <NavIcon name="profile" :filled="onOwnProfile" class="h-6 w-6" />
            <span class="hidden lg:inline">{{ t('nav.myProfile') }}</span>
            <span
              v-if="onOwnProfile"
              class="absolute inset-x-0 bottom-0 h-0.5 bg-primary"
              aria-hidden="true"
            />
          </RouterLink>
        </nav>
      </div>
    </header>

    <!-- Was hardcoded English on the one shell every signed-in page uses. -->
    <p
      v-if="showVerifyBanner"
      class="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-primary-fixed px-4 py-2 text-center text-body-sm text-on-primary-fixed-variant"
      role="status"
    >
      <span>{{ t('nav.verifyEmail') }}</span>
      <RouterLink to="/settings" class="text-label-md text-primary hover:underline">
        {{ t('nav.verifySend') }}
      </RouterLink>
      <button
        type="button"
        class="text-label-sm text-outline hover:underline"
        @click="dismissVerifyBanner"
      >
        {{ t('nav.dismiss') }}
      </button>
    </p>

    <!-- ── Body ────────────────────────────────────────────────────────── -->
    <!--
      Three columns centred as one group, each rail `sticky` inside the
      container rather than `fixed` to the viewport. The rail used to be
      fixed, which meant the layout had to reserve its width as padding and
      the two numbers had to be kept in agreement by hand; this way the grid
      owns the widths and there is nothing to keep in step.

      The left rail appears at `xl` and the right at `lg`: on a laptop the
      people you can talk to are worth more than a second copy of navigation
      that is already in the bar above.
    -->
    <div
      :class="[
        'mx-auto flex w-full flex-1 gap-space-lg px-0 sm:px-4',
        shellWidth,
        isFullBleed ? '' : 'items-start',
      ]"
    >
      <!--
        The rails are wrapped rather than given these classes directly.
        Attribute fallthrough puts them on the component's own root, which is
        already `flex flex-col gap-*` — so `hidden`/`xl:block` and `flex` ended
        up fighting over `display` on one element. `block` won at exactly the
        width the rail becomes visible, the rail stopped being a flex
        container, and the gap between its cards silently collapsed to zero:
        four cards rendered as one slab with hairlines across it.

        A wrapper owns whether the column is there and how wide it is; the rail
        owns how its own cards stack. Neither can undo the other.
      -->
      <!--
        The rail sticks below the chrome and scrolls *itself* once it is taller
        than what is left of the viewport. Without that, reaching the bottom of
        the rail meant scrolling the whole feed to its end first — on a laptop
        the contacts rail was simply never reachable.
      -->
      <div
        v-if="showRails"
        class="sticky hidden w-64 shrink-0 overflow-y-auto overscroll-contain xl:block"
        :style="RAIL_STYLE"
      >
        <SideRail />
      </div>

      <main
        id="main"
        class="min-w-0 flex-1"
        :class="[
          isFullBleed ? '' : 'py-0 sm:py-space-lg',
          columnWidth,
          auth.isAuthenticated && !isFullBleed ? 'pb-20 lg:pb-space-lg' : '',
        ]"
      >
        <slot />
      </main>

      <div
        v-if="showContacts"
        class="sticky hidden w-72 shrink-0 overflow-y-auto overscroll-contain lg:block xl:w-80"
        :style="RAIL_STYLE"
      >
        <ContactsRail />
      </div>
    </div>

    <!-- ── Mobile tab bar ──────────────────────────────────────────────── -->
    <nav
      v-if="auth.isAuthenticated && !isFullBleed && !chatOnly"
      class="tv-safe-bottom fixed inset-x-0 bottom-0 z-30 bg-surface-container-lowest/95 shadow-[0_-1px_8px_rgba(0,0,0,0.05)] backdrop-blur-md md:hidden"
      aria-label="Main"
    >
      <ul class="flex items-stretch">
        <li v-for="item in mobileNav" :key="item.name" class="flex-1">
          <RouterLink
            :to="item.to"
            class="flex flex-col items-center gap-0.5 px-1 py-2 text-label-sm transition-colors"
            :class="isActive(item) ? 'text-primary' : 'text-on-surface-variant'"
            :aria-current="isActive(item) ? 'page' : undefined"
          >
            <span class="relative">
              <NavIcon :name="item.icon" :filled="isActive(item)" class="h-5 w-5" />
              <span class="absolute -right-2 -top-1">
                <NotificationBadge v-if="item.badge" :count="item.badge()" />
              </span>
            </span>
            {{ item.label }}
          </RouterLink>
        </li>
        <li class="flex-1">
          <RouterLink
            v-if="auth.user"
            :to="profilePath"
            class="flex flex-col items-center gap-0.5 px-1 py-2 text-label-sm transition-colors"
            :class="onOwnProfile ? 'text-primary' : 'text-on-surface-variant'"
          >
            <Avatar
              :name="auth.user.displayName"
              :src="auth.user.avatarUrl"
                  :asset="auth.user.avatarAsset"
              :user-id="auth.user.id"
              size="xs"
            />
            {{ t('nav.myProfile') }}
          </RouterLink>
        </li>
      </ul>
    </nav>
  </div>
</template>
